"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_AUTH_BUFFER_PCT = exports.TenantPaymentConfigSchema = exports.ChargeModeSchema = exports.APosTenantPaymentConfigSchema = void 0;
exports.resolveOfferedModes = resolveOfferedModes;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
exports.APosTenantPaymentConfigSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    nmiMerchantId: zod_1.z.string().optional(),
    nmiSecurityKeyRef: zod_1.z.string().optional(),
    chargeMode: zod_1.z.enum(['A', 'B']).optional(),
    offeredModes: zod_1.z.object({ A: zod_1.z.boolean(), B: zod_1.z.boolean() }).optional(),
    authBufferPct: zod_1.z.number().min(0).max(100).optional(),
    onboardingCompletedAt: zod_1.z.number().optional(),
    marketplaceCommissionPct: zod_1.z.number().min(0).max(100).optional(),
    lastWebhookEventId: zod_1.z.string().optional(),
}).merge(timestamps_schema_1.TimestampsSchema);
// ---------------------------------------------------------------------------
// Phase E — runtime-config view of the per-tenant NMI payment config.
//
// This is an ADDITIVE companion to APosTenantPaymentConfigSchema above. The
// existing APos* schema is the persisted (timestamped) DynamoDB record; the
// TenantPaymentConfigSchema below is the lighter shape the ecommerce-service
// checkout/authorize path reads/validates at runtime, where `authBufferPct`
// is a FRACTION in [0,1] (e.g. 0.18) per UNIFIED-MODEL §6.2 "estimate + ~15–20%
// headroom", and timestamps are not required. Kept side-by-side (not a rename)
// so existing consumers of APos* are untouched.
// ---------------------------------------------------------------------------
/** Charge mode per UNIFIED-MODEL §6.2:
 *   A = authorize online → capture at fulfillment (default, allowed verticals)
 *   B = no online charge → pay in person at store/delivery (regulated items) */
exports.ChargeModeSchema = zod_1.z.enum(['A', 'B']);
/** Per-tenant NMI payment config (UNIFIED-MODEL §6.2 "Per-tenant payment config").
 *  Buffer is a fraction (0.15–0.20).
 *  NOTE (S-6): lastWebhookEventId is a coarse last-seen scalar kept for back-compat.
 *  Webhook idempotency MUST be keyed per gatewayTransactionId (processed-events set
 *  / per-order payment row) — a single tenant-level scalar cannot correctly dedupe
 *  independent auth/capture webhooks from a multi-store cart. */
exports.TenantPaymentConfigSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    nmiMerchantId: zod_1.z.string().optional(),
    /** Reference to the encrypted security key (per (tenantId,locationId) creds live on
     *  GATEWAY_CONFIGS_TABLE; this is a tenant-level pointer/marker, not the secret). */
    nmiSecurityKeyRef: zod_1.z.string().optional(),
    chargeMode: exports.ChargeModeSchema.optional(),
    offeredModes: zod_1.z.object({ A: zod_1.z.boolean(), B: zod_1.z.boolean() }).optional(),
    /** Headroom added to the authorized amount. Fraction in [0,1]. Default applied by caller = 0.18. */
    authBufferPct: zod_1.z.number().min(0).max(1).optional(),
    onboardingCompletedAt: zod_1.z.string().datetime().optional(),
    marketplaceCommissionPct: zod_1.z.number().min(0).max(1).optional(),
    lastWebhookEventId: zod_1.z.string().optional(),
});
/** Sensible default applied when a tenant config omits authBufferPct. */
exports.DEFAULT_AUTH_BUFFER_PCT = 0.18;
/** Which payment modes the tenant offers at checkout. Falls back to the legacy
 *  single `chargeMode`, and finally to Mode A only. Guarantees >=1 true. */
function resolveOfferedModes(cfg) {
    if (cfg?.offeredModes && (cfg.offeredModes.A || cfg.offeredModes.B))
        return cfg.offeredModes;
    if (cfg?.chargeMode === 'B')
        return { A: false, B: true };
    if (cfg?.chargeMode === 'A')
        return { A: true, B: false };
    return { A: true, B: false };
}
//# sourceMappingURL=tenant-payment-config.schema.js.map